import { Component, type ErrorInfo, type ReactNode } from "react";
import { withTranslation, type WithTranslation } from "react-i18next";
import { AppButton } from "@/components/ui/primitives";

type ErrorBoundaryProps = WithTranslation & {
  children: ReactNode;
};

type ErrorBoundaryState = {
  hasError: boolean;
};

class ErrorBoundaryBase extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Unhandled render error", error, errorInfo);
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    const { t } = this.props;

    return (
      <main className="app-page flex min-h-screen items-center justify-center px-4 py-12">
        <section className="app-panel-raised w-full max-w-lg space-y-4 p-6 text-center sm:p-8" role="alert" aria-labelledby="boundary-error-title" aria-describedby="boundary-error-description">
          <h1 id="boundary-error-title" className="font-[var(--ds-font-display)] text-[length:var(--ds-type-title-size)] font-medium leading-[var(--ds-type-title-line)] tracking-[var(--ds-type-title-tracking)] text-[var(--app-text)]">
            {t("common:errors.boundaryTitle")}
          </h1>
          <p id="boundary-error-description" className="text-base leading-6 text-[var(--app-text-secondary)]">
            {t("common:errors.boundaryBody")}
          </p>
          <AppButton tone="primary" onClick={this.handleReload}>
            {t("common:errors.reloadPage")}
          </AppButton>
        </section>
      </main>
    );
  }
}

export const ErrorBoundary = withTranslation()(ErrorBoundaryBase);
